import type { RpsState } from "@/lib/types";

// 손 코드 → 이모지 / 한글. 이모지와 한글을 «항상 같이» 쓴다(어르신 화면 배려).
export interface HandDef {
  code: "R" | "S" | "P";
  emoji: string;
  label: string;
}

export const HANDS: HandDef[] = [
  { code: "R", emoji: "✊", label: "바위" },
  { code: "S", emoji: "✌️", label: "가위" },
  { code: "P", emoji: "🖐️", label: "보" },
];

const EMOJI: Record<string, string> = { R: "✊", S: "✌️", P: "🖐️" };
const LABEL: Record<string, string> = { R: "바위", S: "가위", P: "보" };

export function handEmoji(hand?: string): string {
  return EMOJI[hand ?? ""] ?? "";
}

export function handLabel(hand?: string): string {
  return LABEL[hand ?? ""] ?? "";
}

// "✊ 바위" 형태. 서버가 준 hand_txt 가 있으면 그것을 우선한다.
export function handFull(hand?: string, handTxt?: string): string {
  const emoji = handEmoji(hand);
  const label = handTxt || handLabel(hand);
  if (!emoji && !label) return "";
  return `${emoji} ${label}`.trim();
}

// 결과(W/L/D) 표기
export function resultLabel(result?: string, resultTxt?: string): string {
  if (resultTxt) return resultTxt;
  if (result === "W") return "승";
  if (result === "L") return "패";
  if (result === "D") return "무";
  return "";
}

export function resultText(result?: string): string {
  if (result === "W") return "이겼습니다!";
  if (result === "L") return "졌습니다";
  if (result === "D") return "비겼습니다";
  return "";
}

// 결과 뱃지 스타일 (승 = 빨강 강조 / 패 = 회색 / 무 = 중립)
export function resultBadge(result?: string): string {
  if (result === "W") return "bg-accent-500 text-primary-950";
  if (result === "L") return "bg-background-200 text-foreground-500";
  return "bg-secondary-100 text-secondary-900";
}

export const STATE_BORDER: Record<RpsState, string> = {
  open: "border-secondary-400",
  closed: "border-background-200",
};

export const STATE_STRIP: Record<RpsState, string> = {
  open: "bg-secondary-500",
  closed: "bg-background-300",
};

export const STATE_BADGE: Record<RpsState, string> = {
  open: "bg-secondary-100 text-secondary-900",
  closed: "bg-background-200 text-foreground-500",
};

export function stateLabel(state?: string, stateTxt?: string): string {
  if (stateTxt) return stateTxt;
  return state === "closed" ? "마감" : "진행중";
}

export function isDim(state?: string): boolean {
  return state === "closed";
}

export interface RpsCountdown {
  text: string;
  urgent: boolean;
  expired: boolean;
}

// 마감까지 남은 초 표기. 1시간 이상 "1:12:30", 미만 "38:12"
// 10분 미만이면 urgent(빨강 + 깜빡임)
export function formatCountdown(totalSeconds: number): RpsCountdown {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  const urgent = s > 0 && s < 600;
  if (h > 0) return { text: `${h}:${pad(m)}:${pad(sec)}`, urgent, expired: s <= 0 };
  return { text: `${pad(m)}:${pad(sec)}`, urgent, expired: s <= 0 };
}

// "2026-09-14 13:20:00" → "09.14 13:20"
export function formatDeadline(value?: string): string {
  if (!value) return "";
  const d = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}